import { Button, Group, Stack, Text } from '@mantine/core';
import { ContentCard } from '../../../components/common/ContentCard';
import { ArrowRight, Calendar } from 'react-feather';
import { useNavigate } from 'react-router-dom';

/**
 * MED-817 / E4.5 AC14 — Next Session Callout (right panel).
 *
 * Shown only when the peer leader is the designated leader of the next upcoming
 * session in their group (the page gates rendering on a non-null callout from
 * the bundle). Surfaces "You're leading Session N on {date}" and links to that
 * session's prep flow (E4.2). No separate notification email — this is the
 * natural-moment surface.
 *
 * MED-1060 follow-up: uses the shared ContentCard with the standard
 * icon + title + subheading header, while keeping its distinct green-accent
 * identity (medleyGreen background + button). The button is sized to match the
 * other post-session action buttons.
 */

const TITLE_COLOR = '#1A1A2E';
const SUBTITLE_COLOR = '#9CA3AF';

interface Props {
  sessionId: string;
  sessionNumber: number;
  sessionDateIso: string | null;
}

function formatSessionDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function NextSessionCallout({
  sessionId,
  sessionNumber,
  sessionDateIso,
}: Props) {
  const navigate = useNavigate();
  const dateLabel = formatSessionDate(sessionDateIso);

  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      contentGap="sm"
      style={{ backgroundColor: 'var(--mantine-color-medleyGreen-0)' }}
      header={
        <Stack gap={4}>
          <Group gap={8} align="flex-start" wrap="nowrap">
            <Calendar
              size={18}
              color="var(--mantine-color-medleyGreen-6)"
              aria-hidden="true"
              style={{ marginTop: 2 }}
            />
            <Text fz={15} fw={700} c={TITLE_COLOR}>
              {dateLabel
                ? `You're leading Session ${sessionNumber} on ${dateLabel}`
                : `You're leading Session ${sessionNumber}`}
            </Text>
          </Group>
          <Text fz={12} fw={500} c={SUBTITLE_COLOR}>
            Get a head start on preparing to lead the next session.
          </Text>
        </Stack>
      }
    >
      <Button
        fullWidth
        justify="center"
        variant="light"
        color="medleyGreen"
        size="sm"
        rightSection={<ArrowRight size={14} />}
        onClick={() => navigate(`/participant/sessions/${sessionId}/prep`)}
      >
        Go to Session Prep
      </Button>
    </ContentCard>
  );
}

export default NextSessionCallout;
